"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { LEAD_DROP_STEPS } from "@/constants/lead";
import {
  getFormValuesFromCookie,
  isUserVerified as isUserVerifiedFromLeadCookie,
} from "@/lib/userDataCookie";
import { useNonVerifiedCapture } from "@/hooks/useNonVerifiedCapture";

export function useLeaddrop7WidgetState(
  defaultValues?: Record<string, unknown>,
  enrollmentForcedCourseSelectValue?: string | null,
) {
  const searchParams = useSearchParams() || new URLSearchParams();

  const { defaultValues: prefilledData } = useSelector(
    (state: RootState) => state.leaddrop,
  );

  const [formValues, setFormValues] = useState(() => {
    const media =
      (defaultValues || {})?.media || searchParams.get("media") || "Direct";
    const campaign =
      (defaultValues || {})?.campaign ||
      searchParams.get("campaign") ||
      "Direct";
    const forced =
      enrollmentForcedCourseSelectValue != null &&
      String(enrollmentForcedCourseSelectValue).trim() !== ""
        ? String(enrollmentForcedCourseSelectValue).trim()
        : null;
    const base = {
      phone: "",
      name: "",
      email: "",
      qualification: "",
      interest: "",
      course: "",
      media,
      campaign,
      otp: "",
      dynamic: {} as Record<string, string>,
    };
    const withForced = forced
      ? { ...base, interest: forced, course: forced }
      : base;
    if (typeof window === "undefined") return withForced;
    const cookieFv = getFormValuesFromCookie();
    if (!cookieFv) return withForced;
    const dyn = cookieFv.dynamic;
    const fromCookieInterest = String(
      cookieFv.interest ?? cookieFv.course ?? "",
    );
    const fromCookieCourse = String(
      cookieFv.course ?? cookieFv.interest ?? "",
    );
    return {
      ...base,
      phone: String(cookieFv.phone ?? cookieFv.mobile ?? ""),
      name: String(cookieFv.name ?? ""),
      email: String(cookieFv.email ?? ""),
      qualification: String(
        cookieFv.qualification ?? cookieFv.highest_qualification ?? "",
      ),
      interest: forced ?? fromCookieInterest,
      course: forced ?? fromCookieCourse,
      dynamic:
        dyn && typeof dyn === "object" && !Array.isArray(dyn)
          ? (dyn as Record<string, string>)
          : {},
    };
  });

  // Non-verified capture (additive; record-only, does not change this form's flow)
  useNonVerifiedCapture(formValues);

  const [step, setStep] = useState(() => {
    if (typeof window === "undefined") return LEAD_DROP_STEPS.MOBILE_NUMBER;
    if (
      isUserVerifiedFromLeadCookie() ||
      sessionStorage.getItem("isUserVerified") === "true"
    ) {
      return LEAD_DROP_STEPS.LAST_SCREEN;
    }
    return LEAD_DROP_STEPS.MOBILE_NUMBER;
  });

  useEffect(() => {
    if (!prefilledData) return;
    const { phone, name, email, qualification, interest, course } = prefilledData;
    const hasForcedEnrollmentCourse =
      enrollmentForcedCourseSelectValue != null &&
      String(enrollmentForcedCourseSelectValue).trim() !== "";

    setFormValues((prev) => ({
      ...prev,
      ...(phone ? { phone } : {}),
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
      ...(qualification ? { qualification } : {}),
      ...(!hasForcedEnrollmentCourse && interest ? { interest } : {}),
      ...(!hasForcedEnrollmentCourse && course ? { course } : {}),
    }));
  }, [prefilledData, enrollmentForcedCourseSelectValue]);

  useEffect(() => {
    const forced =
      enrollmentForcedCourseSelectValue != null &&
      String(enrollmentForcedCourseSelectValue).trim()
        ? String(enrollmentForcedCourseSelectValue).trim()
        : null;
    if (forced) {
      setFormValues((prev) => ({
        ...prev,
        course: forced,
        interest: forced,
      }));
      return;
    }
    // Forced course cleared (navigated off a Program Enrollments page). The
    // global popup persists its state across client-side navigation, so the
    // previously-bound course would otherwise linger and surface on the next
    // page (e.g. the home thank-you showing the last-visited programme instead
    // of the one actually submitted). Restore the submitted course from cookie.
    if (typeof window === "undefined") return;
    const cookieFv = getFormValuesFromCookie();
    const restored = String(
      cookieFv?.course ?? cookieFv?.interest ?? "",
    ).trim();
    setFormValues((prev) => {
      if (
        String(prev.course ?? "") === restored &&
        String(prev.interest ?? "") === restored
      ) {
        return prev;
      }
      return { ...prev, course: restored, interest: restored };
    });
  }, [enrollmentForcedCourseSelectValue]);

  const [showFields, setShowFields] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(true);
  /**
   * Lives here (not inside Leaddrop7) so it survives the modal closing: the
   * Radix DialogContent unmounts Leaddrop7 on close, which would otherwise
   * reset this flag and re-enable the (already OTP-verified) phone field when
   * the user reopens the "complete your profile" step.
   */
  const [pendingRegistrationAfterOtp, setPendingRegistrationAfterOtp] =
    useState(false);

  return {
    formValues,
    setFormValues,
    step,
    setStep,
    showFields,
    setShowFields,
    termsAccepted,
    setTermsAccepted,
    pendingRegistrationAfterOtp,
    setPendingRegistrationAfterOtp,
  };
}
